/**
 * A small, safe expression language for formula custom fields. Supports numbers, strings,
 * field references, arithmetic, comparisons, and a handful of functions. Never evaluates code.
 *
 *   {Estimate} * 1.5
 *   IF({Priority} = 1, "Urgent", "Normal")
 *   DAYS_UNTIL({Due date})
 *   ROUND({Points} / {Estimate}, 2)
 *   CONCAT({Name}, " (", {Status}, ")")
 */

export type FormulaValue = number | string | boolean | null;

export interface FormulaScope {
  /** Field values by their display name (case-insensitive) and by key. */
  get: (name: string) => FormulaValue;
  now: number;
}

type Token =
  | { kind: 'num'; value: number }
  | { kind: 'str'; value: string }
  | { kind: 'ref'; name: string }
  | { kind: 'ident'; name: string }
  | { kind: 'op'; value: string }
  | { kind: 'lparen' }
  | { kind: 'rparen' }
  | { kind: 'comma' };

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === '{') {
      const end = src.indexOf('}', i);
      if (end < 0) throw new Error('Unclosed field reference');
      out.push({ kind: 'ref', name: src.slice(i + 1, end).trim() });
      i = end + 1;
      continue;
    }
    if (ch === '"' || ch === "'") {
      let j = i + 1;
      let value = '';
      while (j < src.length && src[j] !== ch) {
        value += src[j] === '\\' ? (src[++j] ?? '') : src[j];
        j++;
      }
      if (j >= src.length) throw new Error('Unclosed string');
      out.push({ kind: 'str', value });
      i = j + 1;
      continue;
    }
    if (/[0-9.]/.test(ch)) {
      const m = /^\d*\.?\d+(e[+-]?\d+)?/i.exec(src.slice(i));
      if (!m) throw new Error('Bad number');
      out.push({ kind: 'num', value: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!;
      out.push({ kind: 'ident', name: m[0].toUpperCase() });
      i += m[0].length;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (['<=', '>=', '<>', '!=', '&&', '||'].includes(two)) {
      out.push({ kind: 'op', value: two });
      i += 2;
      continue;
    }
    if ('+-*/%<>=&'.includes(ch)) {
      out.push({ kind: 'op', value: ch });
      i++;
      continue;
    }
    if (ch === '(') out.push({ kind: 'lparen' });
    else if (ch === ')') out.push({ kind: 'rparen' });
    else if (ch === ',') out.push({ kind: 'comma' });
    else throw new Error(`Unexpected "${ch}"`);
    i++;
  }
  return out;
}

type Node =
  | { kind: 'lit'; value: FormulaValue }
  | { kind: 'ref'; name: string }
  | { kind: 'call'; name: string; args: Node[] }
  | { kind: 'bin'; op: string; left: Node; right: Node }
  | { kind: 'neg'; arg: Node };

const PRECEDENCE: Record<string, number> = { '||': 1, '&&': 2, '=': 3, '<>': 3, '!=': 3, '<': 4, '<=': 4, '>': 4, '>=': 4, '&': 5, '+': 6, '-': 6, '*': 7, '/': 7, '%': 7 };

function parse(tokens: Token[]): Node {
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  function primary(): Node {
    const t = next();
    if (!t) throw new Error('Unexpected end of formula');
    if (t.kind === 'num') return { kind: 'lit', value: t.value };
    if (t.kind === 'str') return { kind: 'lit', value: t.value };
    if (t.kind === 'ref') return { kind: 'ref', name: t.name };
    if (t.kind === 'op' && t.value === '-') return { kind: 'neg', arg: primary() };
    if (t.kind === 'lparen') {
      const inner = expression(0);
      if (next()?.kind !== 'rparen') throw new Error('Expected )');
      return inner;
    }
    if (t.kind === 'ident') {
      if (t.name === 'TRUE') return { kind: 'lit', value: true };
      if (t.name === 'FALSE') return { kind: 'lit', value: false };
      if (t.name === 'NULL') return { kind: 'lit', value: null };
      if (peek()?.kind !== 'lparen') throw new Error(`Unknown name ${t.name}`);
      next();
      const args: Node[] = [];
      if (peek()?.kind !== 'rparen') {
        for (;;) {
          args.push(expression(0));
          const sep = next();
          if (sep?.kind === 'rparen') break;
          if (sep?.kind !== 'comma') throw new Error('Expected , or )');
        }
      } else next();
      return { kind: 'call', name: t.name, args };
    }
    throw new Error('Unexpected token');
  }

  function expression(minPrec: number): Node {
    let left = primary();
    for (;;) {
      const t = peek();
      if (!t || t.kind !== 'op') break;
      const prec = PRECEDENCE[t.value];
      if (prec === undefined || prec < minPrec) break;
      next();
      const right = expression(prec + 1);
      left = { kind: 'bin', op: t.value, left, right };
    }
    return left;
  }

  const tree = expression(0);
  if (pos < tokens.length) throw new Error('Unexpected input after the formula');
  return tree;
}

const num = (v: FormulaValue): number => (typeof v === 'number' ? v : typeof v === 'boolean' ? (v ? 1 : 0) : v === null ? 0 : Number(v) || 0);
const str = (v: FormulaValue): string => (v === null ? '' : typeof v === 'number' ? String(v) : typeof v === 'boolean' ? (v ? 'true' : 'false') : v);
const truthy = (v: FormulaValue): boolean => (typeof v === 'boolean' ? v : typeof v === 'number' ? v !== 0 : v !== null && v !== '');
const DAY = 86_400_000;

function evaluate(node: Node, scope: FormulaScope): FormulaValue {
  switch (node.kind) {
    case 'lit':
      return node.value;
    case 'ref':
      return scope.get(node.name);
    case 'neg':
      return -num(evaluate(node.arg, scope));
    case 'bin': {
      const l = evaluate(node.left, scope);
      // Short-circuit logic.
      if (node.op === '&&') return truthy(l) ? truthy(evaluate(node.right, scope)) : false;
      if (node.op === '||') return truthy(l) ? true : truthy(evaluate(node.right, scope));
      const r = evaluate(node.right, scope);
      switch (node.op) {
        case '+':
          return typeof l === 'string' || typeof r === 'string' ? str(l) + str(r) : num(l) + num(r);
        case '-':
          return num(l) - num(r);
        case '*':
          return num(l) * num(r);
        case '/':
          return num(r) === 0 ? null : num(l) / num(r);
        case '%':
          return num(r) === 0 ? null : num(l) % num(r);
        case '&':
          return str(l) + str(r);
        case '=':
          return typeof l === 'string' || typeof r === 'string' ? str(l).toLowerCase() === str(r).toLowerCase() : num(l) === num(r);
        case '<>':
        case '!=':
          return !(typeof l === 'string' || typeof r === 'string' ? str(l).toLowerCase() === str(r).toLowerCase() : num(l) === num(r));
        case '<':
          return num(l) < num(r);
        case '<=':
          return num(l) <= num(r);
        case '>':
          return num(l) > num(r);
        case '>=':
          return num(l) >= num(r);
      }
      throw new Error(`Unknown operator ${node.op}`);
    }
    case 'call': {
      const a = node.args.map((x) => evaluate(x, scope));
      switch (node.name) {
        case 'IF':
          return truthy(a[0] ?? null) ? (a[1] ?? null) : (a[2] ?? null);
        case 'AND':
          return a.every(truthy);
        case 'OR':
          return a.some(truthy);
        case 'NOT':
          return !truthy(a[0] ?? null);
        case 'ISBLANK':
          return a[0] === null || a[0] === '' || a[0] === undefined;
        case 'ROUND': {
          const p = 10 ** num(a[1] ?? 0);
          return Math.round(num(a[0] ?? 0) * p) / p;
        }
        case 'FLOOR':
          return Math.floor(num(a[0] ?? 0));
        case 'CEIL':
          return Math.ceil(num(a[0] ?? 0));
        case 'ABS':
          return Math.abs(num(a[0] ?? 0));
        case 'MIN':
          return a.length ? Math.min(...a.map(num)) : null;
        case 'MAX':
          return a.length ? Math.max(...a.map(num)) : null;
        case 'SUM':
          return a.reduce<number>((s, v) => s + num(v), 0);
        case 'AVG':
          return a.length ? a.reduce<number>((s, v) => s + num(v), 0) / a.length : null;
        case 'CONCAT':
          return a.map(str).join('');
        case 'UPPER':
          return str(a[0] ?? null).toUpperCase();
        case 'LOWER':
          return str(a[0] ?? null).toLowerCase();
        case 'LEN':
          return str(a[0] ?? null).length;
        case 'NOW':
          return scope.now;
        case 'TODAY':
          return Math.floor(scope.now / DAY) * DAY;
        case 'DAYS_UNTIL':
          return a[0] === null || a[0] === undefined ? null : Math.ceil((num(a[0]) - scope.now) / DAY);
        case 'DAYS_SINCE':
          return a[0] === null || a[0] === undefined ? null : Math.floor((scope.now - num(a[0])) / DAY);
        case 'HOURS':
          // Milliseconds (time estimate, time tracked) to hours.
          return num(a[0] ?? 0) / 3_600_000;
        default:
          throw new Error(`Unknown function ${node.name}`);
      }
    }
  }
}

const cache = new Map<string, Node | Error>();

/** Parses once (cached) and evaluates. Errors come back as `{ error }` instead of throwing. */
export function evaluateFormula(expression: string, scope: FormulaScope): { value: FormulaValue; error?: string } {
  let tree = cache.get(expression);
  if (!tree) {
    try {
      tree = parse(tokenize(expression));
    } catch (err) {
      tree = err instanceof Error ? err : new Error(String(err));
    }
    cache.set(expression, tree);
  }
  if (tree instanceof Error) return { value: null, error: tree.message };
  try {
    const value = evaluate(tree, scope);
    return { value: typeof value === 'number' && !Number.isFinite(value) ? null : value };
  } catch (err) {
    return { value: null, error: err instanceof Error ? err.message : 'Formula failed' };
  }
}

/** Checks a formula for syntax errors without evaluating it. */
export function validateFormula(expression: string): string | null {
  try {
    parse(tokenize(expression));
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : 'Invalid formula';
  }
}
